/**
 * Reader workflow state management.
 * Manages payload list, decryption keys, and UI state for readers.
 */

import { setState, getState, batchUpdate } from './store.js';

// State shape (for reference):
// reader: {
//   payloads: [],              // Fetched payload objects
//   privateKey: null,          // Session-only private key (if remembered)
//   rememberKey: false,        // Whether to remember key for session
//   sortColumn: 'pk',          // Current sort column
//   sortDirection: 'desc',     // 'asc' or 'desc'
//   selectedPayload: null,     // Payload being decrypted
//   status: 'idle',            // 'idle' | 'loading' | 'decrypting' | 'generating'
//   error: null
// }

/**
 * Initialize reader state in the store.
 * Call this when the reader workflow is entered.
 */
export function initReaderState() {
  batchUpdate({
    'reader.payloads': [],
    'reader.privateKey': null,
    'reader.rememberKey': false,
    'reader.sortColumn': 'pk',
    'reader.sortDirection': 'desc',
    'reader.selectedPayload': null,
    'reader.status': 'idle',
    'reader.error': null
  });
}

/**
 * Set a single reader field value.
 *
 * @param {string} field - Field name (without 'reader.' prefix)
 * @param {*} value - Value to set
 */
export function setReaderField(field, value) {
  setState(`reader.${field}`, value);
}

/**
 * Set reader workflow status with optional error.
 *
 * @param {'idle'|'loading'|'decrypting'|'generating'} status - Current status
 * @param {string|null} [error=null] - Error message, or null to clear
 */
export function setReaderStatus(status, error = null) {
  batchUpdate({
    'reader.status': status,
    'reader.error': error
  });
}

/**
 * Set the sort column and direction.
 * Toggles direction if same column, otherwise defaults to descending.
 *
 * @param {string} column - Column to sort by
 */
export function setReaderSort(column) {
  const state = getState();
  const currentColumn = state.reader?.sortColumn;
  const currentDirection = state.reader?.sortDirection;

  // Toggle direction if same column, otherwise default to descending
  const newDirection = column === currentColumn && currentDirection === 'desc' ? 'asc' : 'desc';

  batchUpdate({
    'reader.sortColumn': column,
    'reader.sortDirection': newDirection
  });
}

/**
 * Add a payload to the payloads list.
 *
 * @param {Object} payload - Payload object to add
 */
export function addPayload(payload) {
  const state = getState();
  const current = state.reader?.payloads || [];
  setState('reader.payloads', [...current, payload]);
}

/**
 * Remove a payload from the list by partition key.
 *
 * @param {string} pk - Partition key of payload to remove
 */
export function removePayload(pk) {
  const state = getState();
  const current = state.reader?.payloads || [];
  setState('reader.payloads', current.filter(p => p.pk !== pk));
}

/**
 * Set the private key for decryption.
 *
 * @param {string} privateKey - Base64-encoded private key
 * @param {boolean} [rememberForSession=false] - Whether to remember for session
 */
export function setPrivateKey(privateKey, rememberForSession = false) {
  if (rememberForSession) {
    batchUpdate({
      'reader.privateKey': privateKey,
      'reader.rememberKey': true
    });
  } else {
    // Don't store key if not remembering, just set flag
    setState('reader.rememberKey', false);
  }
}

/**
 * Clear the private key and remember flag.
 */
export function clearPrivateKey() {
  batchUpdate({
    'reader.privateKey': null,
    'reader.rememberKey': false
  });
}

/**
 * Clear all reader state (for cleanup on logout or role switch).
 */
export function clearReaderState() {
  batchUpdate({
    'reader.payloads': [],
    'reader.privateKey': null,
    'reader.rememberKey': false,
    'reader.sortColumn': 'pk',
    'reader.sortDirection': 'desc',
    'reader.selectedPayload': null,
    'reader.status': 'idle',
    'reader.error': null
  });
}
